import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { Box, xcss } from '@atlaskit/primitives';

import SearchIcon from '@atlaskit/icon/glyph/search';
import Textfield from '@atlaskit/textfield';
import { useAppUpdatesContext } from '@app/app/src/AppContext';
import { useBundleUrlFilterSync } from '../../hooks';

const messages = defineMessages({
  label: {
    id: 'appUpdates.bundles.search.label',
    defaultMessage: 'Search release notes',
    description: 'Accessibility label for bundled changes search input',
  },
  placeholder: {
    id: 'appUpdates.bundles.search.placeholder',
    defaultMessage: 'Search release notes',
    description: 'Placeholder text for bundled changes search input',
  },
});

const searchContainerStyles = xcss({
  minWidth: '200px',
  maxWidth: '400px',
  width: '100%',
});

const searchIconStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  verticalAlign: 'middle',
  paddingLeft: 'space.100',
});

export const BundleSearchInput: React.FC = () => {
  const { formatMessage } = useIntl();
  const { state, dispatch } = useAppUpdatesContext();
  const { syncFiltersToUrl } = useBundleUrlFilterSync();

  const [localValue, setLocalValue] = React.useState(
    state.bundles.draftFilters.searchQuery || '',
  );

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setLocalValue(value);

      dispatch({
        type: 'BUNDLE_FILTER_SEARCH_SELECT',
        payload: { searchQuery: value || null },
      });
    },
    [dispatch],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        syncFiltersToUrl({
          ...state.bundles.draftFilters,
          cursor: undefined,
        });
        dispatch({ type: 'BUNDLES_FILTERS_APPLY' });
      } else if (event.key === 'Escape') {
        setLocalValue('');
        dispatch({
          type: 'BUNDLE_FILTER_SEARCH_SELECT',
          payload: { searchQuery: null },
        });
      }
    },
    [dispatch, state.bundles.draftFilters, syncFiltersToUrl],
  );

  const prevSearchQueryRef = React.useRef(
    state.bundles.draftFilters.searchQuery,
  );
  React.useEffect(() => {
    const searchQuery = state.bundles.draftFilters.searchQuery;
    if (searchQuery !== prevSearchQueryRef.current) {
      prevSearchQueryRef.current = searchQuery;
      setLocalValue(searchQuery || '');
    }
  }, [state.bundles.draftFilters.searchQuery]);

  return (
    <Box xcss={searchContainerStyles}>
      <Textfield
        testId="app-updates-bundle-search-input"
        aria-label={formatMessage(messages.label)}
        value={localValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={formatMessage(messages.placeholder)}
        elemBeforeInput={
          <Box xcss={searchIconStyles}>
            <SearchIcon label="" size="small" />
          </Box>
        }
        autoComplete="off"
        type="text"
        isCompact={true}
      />
    </Box>
  );
};
